import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  type ClassTestHistoryQuery,
  type ClassTestMineQuery,
  type ClassTestSetupBody,
  type ClassTestWeekQuery,
  type CreateClassTestBody,
  type SaveMarkSheetRow,
  type UpdateClassTestBody,
  classTestsApi,
} from '../class-tests';

export const classTestsKeys = {
  all: ['admin', 'class-tests'] as const,
  options: () => ['admin', 'class-tests', 'options'] as const,
  week: (q: ClassTestWeekQuery) => ['admin', 'class-tests', 'week', q] as const,
  mine: (q: ClassTestMineQuery) => ['admin', 'class-tests', 'mine', q] as const,
  sheet: (id: string) => ['admin', 'class-tests', 'sheet', id] as const,
  history: (q: ClassTestHistoryQuery) => ['admin', 'class-tests', 'history', q] as const,
  historyFilters: (q: Pick<ClassTestHistoryQuery, 'scope' | 'schoolYearId'>) =>
    ['admin', 'class-tests', 'history-filters', q] as const,
};

export function useClassTestOptionsQuery() {
  return useQuery({ queryKey: classTestsKeys.options(), queryFn: () => classTestsApi.options() });
}

export function useClassTestWeekQuery(q: ClassTestWeekQuery, enabled = true) {
  return useQuery({
    queryKey: classTestsKeys.week(q),
    queryFn: () => classTestsApi.week(q),
    enabled: enabled && Boolean(q.date),
    placeholderData: keepPreviousData,
  });
}

export function useMyClassTestsQuery(q: ClassTestMineQuery = {}) {
  return useQuery({ queryKey: classTestsKeys.mine(q), queryFn: () => classTestsApi.mine(q) });
}

export function useClassTestHistoryQuery(q: ClassTestHistoryQuery) {
  return useQuery({
    queryKey: classTestsKeys.history(q),
    queryFn: () => classTestsApi.history(q),
    placeholderData: keepPreviousData,
  });
}

export function useClassTestHistoryFiltersQuery(q: Pick<ClassTestHistoryQuery, 'scope' | 'schoolYearId'>) {
  return useQuery({
    queryKey: classTestsKeys.historyFilters(q),
    queryFn: () => classTestsApi.historyFilters(q),
    placeholderData: keepPreviousData,
  });
}

export function useClassTestSheetQuery(id: string | null | undefined) {
  return useQuery({
    queryKey: classTestsKeys.sheet(id ?? ''),
    queryFn: () => classTestsApi.sheet(id as string),
    enabled: Boolean(id),
    // Editors seed from this once (see useFreshData), so even a cache younger than staleTime is refetched.
    refetchOnMount: 'always',
  });
}

// Every write can move a test in someone's week strip, list or mark sheet, so the whole family goes stale.
function useInvalidateAll() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: classTestsKeys.all });
}

export function useCreateClassTestMutation() {
  const onSuccess = useInvalidateAll();
  return useMutation({ mutationFn: (body: CreateClassTestBody) => classTestsApi.create(body), onSuccess });
}

export function useUpdateClassTestMutation() {
  const onSuccess = useInvalidateAll();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: UpdateClassTestBody }) => classTestsApi.update(id, body),
    onSuccess,
  });
}

export function useCancelClassTestMutation() {
  const onSuccess = useInvalidateAll();
  return useMutation({ mutationFn: (id: string) => classTestsApi.cancel(id), onSuccess });
}

export function useClassTestSetupMutation() {
  const onSuccess = useInvalidateAll();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: ClassTestSetupBody }) => classTestsApi.setup(id, body),
    onSuccess,
  });
}

export function useSaveMarkSheetMutation() {
  const onSuccess = useInvalidateAll();
  return useMutation({
    mutationFn: ({ id, rows }: { id: string; rows: SaveMarkSheetRow[] }) => classTestsApi.saveSheet(id, rows),
    onSuccess,
  });
}

export function usePublishClassTestResultsMutation() {
  const onSuccess = useInvalidateAll();
  return useMutation({ mutationFn: (id: string) => classTestsApi.publish(id), onSuccess });
}

export function useUnpublishClassTestResultsMutation() {
  const onSuccess = useInvalidateAll();
  return useMutation({ mutationFn: (id: string) => classTestsApi.unpublish(id), onSuccess });
}
